import { observer } from 'mobx-react';
import { ChangeEvent, Component } from 'react';
import { ICONS } from 'src/constants/icons';
import { localeStore } from 'src/mobx/localesStore';
import ButtonComponent from 'src/view/components/Controls/Button/Button';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Notes from 'src/view/components/Controls/Notes/Notes';
import PopupModal from 'src/view/components/Controls/Popup/Popup';
import TextArea from 'src/view/components/Controls/TextArea/TextArea';
import './style.scss';

interface Props {
  value: string;
  loading?: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
}

interface State {
  value: string;
  modalVisible: boolean;
}

@observer
export default class ProductNotes extends Component<Props, State> {
  state = {
    value: this.props.value,
    modalVisible: false,
  };

  setModalVisible = (modalVisible: boolean) => () => {
    this.setState({ modalVisible });
    this.setState({ value: this.props.value });
  };

  /**
   * Set notes and save in cartStore
   * Method for Notes Component
   *
   * @param {string} notes
   * @memberof CheckoutNotes
   */
  onNotesChanged = (notes: string) => {
    this.setState({ value: notes });
    this.props.onChange(notes);
  };

  /**
   * Set notes if it changed
   *
   * @param {ChangeEvent<HTMLTextAreaElement>} event
   * @memberof CheckoutNotes
   */
  onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ value: event.target.value });
  };

  onClear = () => {
    this.props.onChange('');
    this.setState({ modalVisible: false, value: '' });
  };

  /**
   * Save notes in cartStore
   *
   * @memberof CheckoutNotes
   */
  onSubmit = () => {
    this.props.onChange(this.state.value);
    this.setState({ modalVisible: false });
  };

  /**
   * For render notes in Popup
   *
   * @memberof CheckoutNotes
   * @returns {Component} notes component
   */
  renderPopupContent = () => {
    const { value } = this.state;
    const { disabled } = this.props;
    return (
      <>
        <div className="full-width px-2 pb-3">
          <div className="d-flex flex-row justify-content-between">
            <p className="text-size-24 family-bold mb-3">{localeStore.t(disabled ? 'notes' : 'write_a_note')}</p>
            {!disabled ? <Icon name={ICONS.TRASH} onPress={this.onClear} /> : null}
          </div>
          <div className="full-width">
            {!disabled ? (
              <>
                <TextArea
                  className="product-notes-textarea mb-4"
                  value={value}
                  placeholder={localeStore.t('write_a_note')}
                  onChange={this.onChange}
                />

                <ButtonComponent
                  style={{ width: '100%' }}
                  title={localeStore.t('Submit')}
                  onClick={this.onSubmit}
                  disabled={!value}
                />
              </>
            ) : (
              <p className="text-size-16 color-dark mb-2">{value}</p>
            )}
          </div>
        </div>
      </>
    );
  };

  render() {
    const { value, modalVisible } = this.state;
    return (
      <div className="product-notes">
        <div className="d-none d-lg-flex">
          <Notes
            id={'product_notes'}
            loading={!!this.props.loading}
            notes={value}
            autosize
            onChange={this.onNotesChanged}
            disabled={this.props.disabled}
          />
        </div>
        <div className="d-flex d-lg-none mb-1">
          <div
            className="d-flex flex-row align-items-center justify-content-between full-width"
            onClick={this.setModalVisible(true)}
          >
            <div className="flex-column">
              <p className="text-size-16 family-semibold">{localeStore.t('notes')}</p>
              {value ? <p className="text-size-14 color-gray flex-nowrap product-notes-text ">{value}</p> : null}
            </div>
            <Icon name={ICONS.CHEVRON} className="back-arrow arrow-right" color={'#000000'} />
          </div>
          <PopupModal
            position={'top'}
            direction={'down'}
            justify={'center'}
            selfClose={false}
            backdrop
            visible={modalVisible}
            onClose={this.setModalVisible(false)}
            content={this.renderPopupContent}
            mobile={{
              popup: true,
              sheet: true,
            }}
          />
        </div>
      </div>
    );
  }
}
